import { Controller } from "@hotwired/stimulus";
import { Calendar } from "vanilla-calendar-pro";
import { MOBILE_MEDIA_QUERY } from "../lib/breakpoints";

export default class extends Controller {
  static values = {
    selectedDates: Array,
    dateToday: String,
    dateMin: String,
    calendarType: String,
    selectionMode: String,
    disableAllDates: Boolean,
    enableDates: Array,
    disableDates: Array,
    disableDatesGaps: Boolean,
    disablePastDates: Boolean,
    enableEdgeDatesOnly: Boolean,
    closeOnApply: Boolean,
    dispatchOnSelect: Boolean,
    maxSelectedDates: Number,
    limitMessage: String
  }

  static targets = ["calendar"];

  connect() {
    let previousSelectedDates = [...(this.selectedDatesValue || [])];
    this.mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.calendar = new Calendar(this.calendarTarget, {
      inputMode: false,
      selectedTheme: 'light',
      // Month arrows look like the app's other outline switch buttons.
      styles: {
        arrowPrev: "vc-arrow vc-arrow_prev btn btn-outline-switch",
        arrowNext: "vc-arrow vc-arrow_next btn btn-outline-switch"
      },
      type: this.responsiveType(),
      positionToInput: 'auto',
      displayDatesOutside: false,
      disableDatesPast: this.hasDisablePastDatesValue ? this.disablePastDatesValue : true,
      ...(this.hasDateTodayValue ? { dateToday: this.dateTodayValue } : {}),
      ...(this.hasDateMinValue ? { dateMin: this.dateMinValue } : {}),
      selectionDatesMode: this.selectionModeValue || "single",
      selectedDates: this.selectedDatesValue,
      firstWeekday: 0,
      selectedWeekends: [],
      disableAllDates: this.disableAllDatesValue || false,
      enableDates: this.enableDatesValue || null,
      disableDates: this.disableDatesValue || null,
      enableEdgeDatesOnly: this.enableEdgeDatesOnlyValue || false,
      disableDatesGaps: this.disableDatesGapsValue || false,
      onClickDate: (self) => {
        const selectedDates = Array.from(self.context.selectedDates || [])
        if (this.hasMaxSelectedDatesValue && selectedDates.length > this.maxSelectedDatesValue) {
          self.set({ selectedDates: previousSelectedDates })
          this.dispatch("limit", {
            detail: {
              maximum: this.maxSelectedDatesValue,
              message: this.limitMessageValue || `You can select up to ${this.maxSelectedDatesValue} dates.`
            }
          })
          return
        }

        previousSelectedDates = selectedDates

        if (this.hasDispatchOnSelectValue && this.dispatchOnSelectValue) {
          this.change()
        }
      }
    });
    this.calendar.init();
    this.mobileQuery.addEventListener("change", this.applyResponsiveType);
  }

  disconnect() {
    this.mobileQuery?.removeEventListener("change", this.applyResponsiveType);
  }

  // Multi-month calendars collapse to a single month on phones.
  responsiveType() {
    const type = this.calendarTypeValue || "default";
    return type === "multiple" && this.mobileQuery.matches ? "default" : type;
  }

  applyResponsiveType = () => {
    // Keep the current selection and visible month instead of the initial ones.
    this.calendar.set({ type: this.responsiveType() }, { dates: false, month: false, year: false });
  }

  change() {
    const selectedDates = this.selectedDates()
    const startDate = selectedDates.at(0)
    const endDate = selectedDates.at(-1) || null

    this.dispatch("change", {
      detail: { selectedDates, startDate, endDate }
    })
  }

  selectedDates() {
    return Array.from(this.calendar?.context.selectedDates || [])
  }

  apply() {
    this.change()
    if (this.hasCloseOnApplyValue ? this.closeOnApplyValue : true) {
      this.element.hidePopover()
    }
  }

  popoverToggled(event) {
    if (event.newState === "closed") {
      this.change()
    }
  }
}

